import { rootApi } from '../../services/rootApi';
import { taskApi, taskResultsApi } from '../../utils/urls';
import { TASK_TYPE } from '../../utils/constants';

/**
 * RTK Query API slice for tasks
 */
export const tasksApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchTasks: builder.query({
      query: ({ courseId }) => ({
        url: taskApi(),
        method: 'POST',
        body: {
          data: { courseId },
          method: 'read'
        },
        // Always suppress error toast for tasks API since it's supplementary data
        // Components can show inline error messages if needed
        suppressGenericErrorToast: true,
      }),
      transformResponse: (response) => {
        const tasks = [];
        const taskUserCounts = [];
        const questions = [];
        const userMap = new Map();

        Object.entries(response.data).forEach(([id, task]) => {
          const usersResults = {};
          const allLastAnswerGrades = {};

          task.taskResults.forEach(result => {
            const { user, feedback, body, created, bodyFiles, feedbackFiles, id: resultId, points } = result;
            const lastUpdate = feedback?.created
              ? { type: 'feedback', date: feedback.created }
              : { type: 'response', date: created };

            let grade = feedback.grade;
            let value = body.value;

            if (task.type !== TASK_TYPE.OPEN && task.type !== TASK_TYPE.FILE) {
              grade = value.toString() === task.correctResponse.value.toString() ? 1 : 0;
              value = body.value.map(item => task.caption.items[item]);
            }

            allLastAnswerGrades[user.id] = grade !== undefined ? grade : 99;

            usersResults[user.id] = {
              name: user.name,
              id: user.id,
              taskTitle: task.caption,
              avatar: user.avatarUrl,
              taskType: task.type,
              taskId: id,
              lastAnswerGrade: grade,
              hasBeenApproved: allLastAnswerGrades[user.id],
              lastUpdate,
              answers: {
                ...(usersResults[user.id]?.answers || {}),
                [resultId]: {
                  id: resultId,
                  points,
                  value,
                  grade, // 1 === correct/approved
                  files: bodyFiles,
                  created,
                  feedback: { ...feedback, files: feedbackFiles },
                }
              }
            };
          });

          const results = Object.values(usersResults);
          taskUserCounts.push(results.length);
          questions.push({
            ...task.caption,
            taskType: task.type,
            taskId: task.id
          });

          tasks.push({
            id: +id,
            type: task.type,
            pageId: task.pageId,
            sectionId: task.sectionId,
            maxPoints: task.maxPoints,
            createdBy: task.createdBy,
            courseId: task.courseId,
            correctOptions: task.correctResponse?.value,
            question: task.caption.main,
            comment: task.caption.comment,
            options: { ...task.caption.items },
            usersCount: results.length,
            users: results,
            needsAttention: Object.values(allLastAnswerGrades).filter(item => item === 99),
            incorrect: Object.values(allLastAnswerGrades).filter(item => item === 0),
          });

          results.forEach(user => {
            if (!userMap.has(user.id)) {
              userMap.set(user.id, {
                name: user.name,
                id: user.id,
                avatar: user.avatar,
                tasks: {}
              });
            }

            const currentUser = userMap.get(user.id);
            currentUser.tasks[user.taskId] = {
              lastAnswerGrade: user.lastAnswerGrade,
              hasBeenApproved: user.hasBeenApproved,
              id: user.taskId,
              lastUpdate: user.lastUpdate,
              type: user.taskType,
              title: user.taskTitle,
              answers: { ...user.answers }
            };
          });
        });

        return {
          tasks,
          taskUserCounts,
          questions,
          users: Array.from(userMap.values())
        };
      },
      providesTags: (result, error, { courseId }) => [
        { type: 'Tasks', id: courseId },
        ...(result?.tasks?.map(task => ({ type: 'Task', id: task.id })) || [])
      ],
    }),

    saveTaskFeedback: builder.mutation({
      query: ({ feedbackData, courseId }) => ({
        url: taskResultsApi(),
        method: 'POST',
        body: {
          data: { ...feedbackData },
          method: 'assess'
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'Tasks', id: courseId }
      ],
    }),

    answerOnTask: builder.mutation({
      query: ({ pmid, taskId, value, isQuiz }) => ({
        url: taskApi(),
        method: 'POST',
        body: {
          data: {
            body: { value },
            pmid,
            taskId
          },
          method: 'answer'
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'Tasks', id: courseId }
      ],
    }),

    updateTask: builder.mutation({
      query: ({ id, type, caption, correctResponse }) => {
        const data = { id };
        if (type !== undefined) data.type = type;
        if (caption !== undefined) data.caption = caption;
        if (correctResponse !== undefined) data.correctResponse = correctResponse;

        return {
          url: taskApi(),
          method: 'POST',
          body: {
            data,
            method: 'update'
          },
        };
      },
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Module', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useFetchTasksQuery,
  useSaveTaskFeedbackMutation,
  useAnswerOnTaskMutation,
  useUpdateTaskMutation,
} = tasksApi;
